import type { PropsWithChildren } from "react";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { cx } from "@/utils/cx";
import { formatComparisonTip, formatMoneyShort, formatSignedMoneyShort, formatSignedPercent } from "../lib/format";
import type { Mover } from "../lib/movers";
import { type RegionSummary, regionCode, regionName } from "../lib/regions";
import { ChangePill, RegionBadge } from "./parts";

const Panel = ({ title, subtitle, children }: PropsWithChildren<{ title: string; subtitle: string }>) => (
  <section className="flex min-w-0 flex-1 flex-col gap-6 rounded-[14px] bg-[#2c1f40] p-7 text-white shadow-[0_2px_12px_rgba(66,48,125,0.14)]">
    <header className="flex flex-col gap-1">
      <h3 className="text-lg leading-7 font-medium">{title}</h3>
      <p className="text-[13px] leading-5 text-white/60">{subtitle}</p>
    </header>
    {children}
  </section>
);

const comparisonTip = (currency: string, converted: number | null, totalsCurrency: string) =>
  currency === totalsCurrency || converted === null ? undefined : formatComparisonTip(converted, totalsCurrency);

type GmvByRegionPanelProps = {
  regions: RegionSummary[];
  totalsCurrency: string;
  selected: string[];
  comparison: string;
  onPick: (key: string) => void;
};

export function GmvByRegionPanel({ regions, totalsCurrency, selected, comparison, onPick }: GmvByRegionPanelProps) {
  const singleRegion = regions.length < 2;
  const subtitle = singleRegion
    ? `Shop GMV vs ${comparison}`
    : `Shop GMV vs ${comparison} · select a region to filter this page`;

  return (
    <Panel title="GMV by region" subtitle={subtitle}>
      <div role="group" aria-label="Regions" className="mt-auto flex flex-col gap-1">
        {regions.map((region) => {
          const isSelected = selected.includes(region.key);
          const dimmed = selected.length > 0 && !isSelected;
          return (
            <button
              key={region.key}
              type="button"
              disabled={singleRegion}
              aria-pressed={singleRegion ? undefined : isSelected}
              onClick={() => onPick(region.key)}
              className={cx(
                "grid grid-cols-[108px_minmax(0,1fr)_40px_64px_auto] items-center gap-4 rounded-lg px-2 py-2 text-left transition-[opacity,background-color] duration-150",
                !singleRegion && "cursor-pointer hover:bg-white/5",
                isSelected && "bg-white/8",
                dimmed && "opacity-40",
              )}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm">{regionName(region.key)}</span>
                <span className="block text-xs text-white/50">
                  {region.merchantCount} {region.merchantCount === 1 ? "merchant" : "merchants"}
                </span>
              </span>
              <span aria-hidden="true" className="h-2.5 overflow-hidden rounded-full bg-white/8">
                <span
                  className="block h-full rounded-full bg-[#c0a7f2] transition-[width] duration-500"
                  style={{ width: `${Math.max(1, region.share ?? 0)}%` }}
                />
              </span>
              <span className="text-sm tabular-nums">
                {region.share === null ? "—" : `${Math.round(region.share)}%`}
              </span>
              <span
                title={comparisonTip(region.currency, region.converted, totalsCurrency)}
                className="text-right text-sm font-medium tabular-nums"
              >
                {formatMoneyShort(region.current, region.currency)}
              </span>
              <ChangePill change={region.change} tone="dark" title={`vs ${comparison}`} />
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

export type MoverRow = Mover & { name: string; logoUrl: string | null; regionKey: string };

type BiggestMoversPanelProps = {
  totalsCurrency: string;
  gaining: MoverRow[];
  declining: MoverRow[];
  comparison: string;
  scope: string[];
  onPick: (merchantId: string) => void;
};

type MoverButtonProps = { mover: MoverRow; totalsCurrency: string; onPick: (merchantId: string) => void };

const MoverButton = ({ mover, totalsCurrency, onPick }: MoverButtonProps) => (
  <button
    type="button"
    aria-label={`See ${mover.name} in the merchant table`}
    onClick={() => onPick(mover.merchantId)}
    className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-white/5"
  >
    <MerchantLogo name={mover.name} logoUrl={mover.logoUrl} size="xs" className="size-5 rounded-md" />
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span className="min-w-0 truncate text-sm">{mover.name}</span>
      <RegionBadge regionKey={mover.regionKey} tone="dark" />
    </span>
    <span
      title={comparisonTip(mover.currency, mover.converted, totalsCurrency)}
      className={cx("text-sm font-medium tabular-nums", mover.diff >= 0 ? "text-[#75e0a7]" : "text-[#fda29b]")}
    >
      {formatSignedMoneyShort(mover.diff, mover.currency)}
    </span>
    <span className="min-w-[52px] text-right text-xs text-white/50 tabular-nums">
      {mover.change === null ? "" : formatSignedPercent(mover.change)}
    </span>
  </button>
);

export function BiggestMoversPanel({
  totalsCurrency,
  gaining,
  declining,
  comparison,
  scope,
  onPick,
}: BiggestMoversPanelProps) {
  const scopeLabel = scope.length ? scope.map(regionCode).join(", ") : "all regions";

  return (
    <Panel title="Biggest movers" subtitle={`Change in shop GMV vs ${comparison} · ${scopeLabel}`}>
      <div className="flex flex-col">
        {gaining.length > 0 ? (
          <>
            <p className="mb-1 px-2 text-[11px] font-semibold tracking-[0.1em] text-white/50 uppercase">Gaining</p>
            {gaining.map((mover) => (
              <MoverButton key={mover.merchantId} mover={mover} totalsCurrency={totalsCurrency} onPick={onPick} />
            ))}
          </>
        ) : (
          <p className="px-2 text-sm text-white/60">No merchant gained GMV.</p>
        )}
        {declining.length > 0 ? (
          <>
            <p className="mt-[22px] mb-1 px-2 text-[11px] font-semibold tracking-[0.1em] text-white/50 uppercase">
              Declining
            </p>
            {declining.map((mover) => (
              <MoverButton key={mover.merchantId} mover={mover} totalsCurrency={totalsCurrency} onPick={onPick} />
            ))}
          </>
        ) : (
          <p className="mt-[22px] px-2 text-sm text-white/60">No merchant lost GMV.</p>
        )}
      </div>
    </Panel>
  );
}
